/**
 * VistoeraTASK-00525 — display-side mirror of the backend Team Management
 * authority rules. These helpers only decide what the UI SHOWS; every action is
 * authorised again by the backend from the live membership row, so tampering
 * with this (or with localStorage / the URL) never grants anything.
 */
import type { TeamMember, TeamRoleKey } from './teamTypes'

export interface TeamAccessInput {
  team_mode_enabled?: boolean | null
  my_role?: string | null
}

/** Left-nav entry + page: Team Mode must be entitled AND the caller Owner/Admin. */
export function canAccessTeamManagement(ws: TeamAccessInput | null | undefined): boolean {
  if (!ws || !ws.team_mode_enabled) return false
  return ws.my_role === 'owner' || ws.my_role === 'admin'
}

export function roleKeyOf(role: string | null | undefined): TeamRoleKey {
  if (role === 'owner') return 'owner'
  if (role === 'admin') return 'admin'
  return 'member'
}

/** Owner manages Admin + Member; Admin manages Member only; nobody manages the Owner. */
export function canManageTarget(actorRole: string | null | undefined, target: Pick<TeamMember, 'role' | 'is_self'>): boolean {
  if (actorRole !== 'owner' && actorRole !== 'admin') return false
  if (target.is_self) return false
  if (target.role === 'owner') return false
  if (target.role === 'admin' && actorRole !== 'owner') return false
  return true
}

/**
 * VistoeraTASK-00638 / 00672 — who may edit whose Member Grant (nodes/channels).
 * Owner Canvas uses Workspace Effective (bypass Member Grant) — team UI never
 * edits the Owner's own Member Grant (TASK-00672). Admin never edits Owner or
 * other Admins.
 */
export function canManageMemberAuth(
  actorRole: string | null | undefined,
  target: Pick<TeamMember, 'role' | 'is_self'>,
): boolean {
  if (actorRole !== 'owner' && actorRole !== 'admin') return false
  if (target.role === 'owner') return false
  if (target.role === 'admin') return actorRole === 'owner'
  return true
}

/** Who may open a member in the auth matrix picker (view + team switch context). */
export function canSelectMemberAuthTarget(
  actorRole: string | null | undefined,
  target: Pick<TeamMember, 'role' | 'is_self'>,
): boolean {
  if (actorRole === 'owner') return true
  if (actorRole !== 'admin') return false
  if (target.role === 'owner' || target.role === 'admin') return false
  return true
}

/** Only the Owner appoints / revokes Admins. */
export function canSetRole(actorRole: string | null | undefined, target: Pick<TeamMember, 'role' | 'is_self'>): boolean {
  return actorRole === 'owner' && !target.is_self && target.role !== 'owner'
}

/** VistoeraTASK-00556 — 成员项目 is Workspace-OWNER-only (Admins reach 团队管理 but
 * never other members' projects). Display-side mirror; the API enforces it. */
export function canViewMemberProjects(role: string | null | undefined): boolean {
  return role === 'owner'
}

export const TEAM_TABS = [
  { key: 'overview', path: '', enabled: true },
  { key: 'members', path: 'members', enabled: true },
  { key: 'credits', path: 'credits', enabled: true },
  { key: 'permissions', path: 'permissions', enabled: true },
  { key: 'usage', path: 'usage', enabled: true },
  { key: 'settings', path: 'settings', enabled: true },
  { key: 'memberProjects', path: 'member-projects', enabled: true, ownerOnly: true },
  { key: 'logs', path: 'logs', enabled: true },
] as const

/** Tabs the caller may see (Owner-only tabs are hidden for Admins). */
export function visibleTeamTabs(role: string | null | undefined) {
  return TEAM_TABS.filter((tab) => !('ownerOnly' in tab && tab.ownerOnly) || canViewMemberProjects(role))
}

export type TeamTabKey = (typeof TEAM_TABS)[number]['key']

export function resolveTeamTab(pathname: string, teamBase: string): TeamTabKey {
  const rest = pathname.startsWith(teamBase) ? pathname.slice(teamBase.length).replace(/^\/+|\/+$/g, '') : ''
  const first = rest.split('/')[0]
  const hit = TEAM_TABS.find((t) => t.path === first && t.enabled)
  return hit ? hit.key : 'overview'
}
